import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import { ScrollToTopButton } from "../../styledComponents/common";

const ScrollToTop = () => {
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [isAnimatingIn, setIsAnimatingIn] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const debounceRef = useRef(null);
  const location = useLocation();

  // Define pages where scroll to top should be active
  const scrollEnabledPages = ["/timesheet/daily"];

  const isScrollEnabled = scrollEnabledPages.some(page =>
    location.pathname.includes(page),
  );

  const checkScrollVisibility = useCallback(() => {
    if (!isScrollEnabled) {
      setShowScrollButton(false);
      return;
    }

    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;

    // Calculate how close to bottom (within 50px tolerance)
    const distanceFromBottom = documentHeight - (scrollTop + windowHeight);
    const isNearBottom = distanceFromBottom <= 50;

    // Show button when user is at or very close to the bottom of the page
    // and there's substantial content (page is scrollable)
    const shouldShow = isNearBottom && documentHeight > windowHeight + 200;

    setShowScrollButton(shouldShow);
  }, [isScrollEnabled]);

  useEffect(() => {
    if (showScrollButton) {
      setShouldRender(true);
      setIsAnimatingIn(false);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsAnimatingIn(true);
        });
      });
    } else {
      setIsAnimatingIn(false);
      setTimeout(() => {
        setShouldRender(false);
      }, 300);
    }
  }, [showScrollButton]);

  useEffect(() => {
    if (!isScrollEnabled) {
      setShowScrollButton(false);
      return;
    }

    // Initial check
    const timer = setTimeout(() => {
      checkScrollVisibility();
    }, 100);

    const handleScroll = () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      debounceRef.current = setTimeout(() => {
        checkScrollVisibility();
      }, 150);
    };

    const handleResize = () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      debounceRef.current = setTimeout(() => {
        checkScrollVisibility();
      }, 200);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleResize, { passive: true });

    return () => {
      clearTimeout(timer);
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleResize);
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [isScrollEnabled, checkScrollVisibility]);

  // Reset state when location changes
  useEffect(() => {
    setShowScrollButton(false);
    setIsAnimatingIn(false);
    setShouldRender(false);
  }, [location.pathname]);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  if (!shouldRender && !isAnimatingIn) return null;

  return (
    <ScrollToTopButton $isVisible={isAnimatingIn} onClick={scrollToTop}>
      <span>Scroll to top</span>
    </ScrollToTopButton>
  );
};

export default ScrollToTop;
